import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { describeLoadFailure, jsonOrThrow } from '../apiJson';
import { trackGenerateLead } from '../ga';
import {
  clearSearchWatching,
  getSavedAlertEmail,
  isSearchWatching,
  markSearchWatching,
  setSavedAlertEmail,
} from '../productWatchIntent';
import { BellIcon } from '../searchAlertBenefits';
import { FacebookGeneralDealsNudge } from './FacebookGeneralDealsNudge';
import './FavoriteSignupModal.css';
import './SearchAlertSignupPanel.css';
import './SearchAlertSignupModal.css';

/**
 * @param {string} heading
 */
function buildSearchAlertCopy(heading) {
  return {
    shortLabel: 'Search alert',
    inviteHeading: 'Get deals before everyone else?',
    inviteSubline:
      'Know when new products or price drops match this search.',
    inviteAction: 'Watch this search',
    emailHeading: 'Where should we send it?',
    emailSummary:
      'Not a newsletter \u2014 only when something new matches or prices drop. Unsubscribe anytime.',
    doneSubline: (email) => {
      const emailBit = email !== '' ? email : 'your email';
      return `Sorry \u2014 search alerts aren\u2019t live yet. We\u2019ve saved ${emailBit} for ${heading} and will email you the moment they\u2019re ready.`;
    },
  };
}

/**
 * @param {{
 *   onClose: () => void;
 *   searchPath: string;
 *   scopeLabel: string;
 *   scopeChips?: string[];
 * }} props
 */
export function SearchAlertSignupModal({
  onClose,
  searchPath,
  scopeLabel,
  scopeChips = [],
}) {
  const titleId = useId();
  const emailId = useId();
  const emailRef = useRef(null);

  const heading =
    typeof scopeLabel === 'string' && scopeLabel.trim() !== ''
      ? scopeLabel.trim()
      : 'this search';

  const copy = buildSearchAlertCopy(heading);

  const chips = Array.isArray(scopeChips)
    ? scopeChips.filter((c) => typeof c === 'string' && c.trim() !== '')
    : [];

  const [step, setStep] = useState(() =>
    isSearchWatching(searchPath) ? 'email' : 'invite',
  );
  const [email, setEmail] = useState(() => getSavedAlertEmail());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submittedEmail, setSubmittedEmail] = useState('');

  useEffect(() => {
    setStep(isSearchWatching(searchPath) ? 'email' : 'invite');
    setEmail(getSavedAlertEmail());
    setError(null);
    setSubmitting(false);
    setSubmittedEmail('');
  }, [searchPath]);

  useEffect(() => {
    if (step !== 'email') {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      emailRef.current?.focus({ preventScroll: true });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [step]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleWatch() {
    markSearchWatching(searchPath);
    setStep('email');
    setError(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const trimmedEmail = email.trim();

    try {
      const res = await fetch('/api/search-alert-signup.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          searchPath,
          scopeLabel: heading,
        }),
      });
      await jsonOrThrow(res);
      setSavedAlertEmail(trimmedEmail);
      clearSearchWatching(searchPath);
      setSubmittedEmail(trimmedEmail);
      setStep('done');
      trackGenerateLead('search_alert');
    } catch (err) {
      setError(describeLoadFailure(err));
    } finally {
      setSubmitting(false);
    }
  }

  let modalHeading = copy.inviteHeading;
  let subline = step === 'invite' ? copy.inviteSubline : null;
  let primaryLabel = copy.inviteAction;

  if (step === 'email') {
    modalHeading = copy.emailHeading;
    subline = copy.emailSummary;
    primaryLabel = 'Notify me';
  } else if (step === 'done') {
    modalHeading = 'Thanks for signing up';
    subline = copy.doneSubline(submittedEmail);
    primaryLabel = 'Got it';
  }

  return createPortal(
    <div
      className="favorite-modal__backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`favorite-modal favorite-modal--search-alert${
          step === 'invite' ? ' search-alert-modal--invite' : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="favorite-modal__header search-alert-modal__header">
          <div className="search-alert-modal__heading">
            <div className="search-alert-panel__icon" aria-hidden="true">
              <BellIcon />
            </div>
            <div className="search-alert-modal__heading-copy">
              <p className="search-alert-modal__kicker">{copy.shortLabel}</p>
              <h2 id={titleId} className="search-alert-panel__title">
                {modalHeading}
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="favorite-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="favorite-modal__body search-alert-modal__body">
          {subline != null && (
            <p className="search-alert-panel__lede search-alert-modal__summary">
              {subline}
            </p>
          )}

          {chips.length > 0 && (
            <ul
              className={`search-alert-panel__chips${
                step === 'invite' ? ' search-alert-modal__invite-chips' : ''
              }`}
              aria-label="Alert scope"
            >
              {chips.map((chip) => (
                <li key={chip} className="search-alert-panel__chip">
                  {chip}
                </li>
              ))}
            </ul>
          )}

          <div className="search-alert-modal__actions">
            {step === 'invite' && (
              <button
                type="button"
                className="search-alert-panel__submit"
                onClick={handleWatch}
              >
                {primaryLabel}
              </button>
            )}

            {step === 'email' && (
              <>
                {error !== null && (
                  <p className="search-alert-panel__error" role="alert">
                    {error}
                  </p>
                )}
                <form
                  className="search-alert-panel__form search-alert-modal__form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <label className="sr-only" htmlFor={emailId}>
                    Email for search alerts
                  </label>
                  <input
                    ref={emailRef}
                    id={emailId}
                    name="email"
                    className="search-alert-panel__input"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    required
                    maxLength={255}
                    disabled={submitting}
                  />
                  <button
                    type="submit"
                    className="search-alert-panel__submit"
                    disabled={submitting}
                  >
                    {submitting ? 'Saving\u2026' : primaryLabel}
                  </button>
                </form>
              </>
            )}

            {step === 'done' && (
              <>
                <button
                  type="button"
                  className="search-alert-panel__submit search-alert-modal__close-btn"
                  onClick={onClose}
                >
                  {primaryLabel}
                </button>

                <FacebookGeneralDealsNudge
                  location="search_alert_modal_done"
                  className="search-alert-panel__facebook--bottom search-alert-modal__done-facebook"
                  title="In the meantime"
                  lede="Search alerts are still on the way — follow us on Facebook for Malta deals we spot in the meantime, from price drops to new local shops."
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
